import * as React from "react";

/**
 * Verarbeitet Inline-Formatierung in einem Text-String:
 * - **bold** → <strong>
 * - [N] → hochgestellter, klickbarer Link zur Quelle #quelle-N
 */
export const processInlineFormatting = (
  text: string,
  quellePrefix: string = "quelle"
): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  // Matches **bold** oder [N] oder [N, S.XX] oder [N, S. 35, 52]
  const regex = /(\*\*([^*]+)\*\*)|(\[(\d+)(?:[^\]]*)\])/g;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    if (match[1]) {
      // **bold**
      parts.push(<strong key={`b-${match.index}`}>{match[2]}</strong>);
    } else if (match[3]) {
      // [N] oder [N, S.XX]
      const num = match[4];
      const label = match[3]; // z.B. "[1]" oder "[1, S.69]"
      parts.push(
        <sup key={`r-${match.index}`}>
          <a
            href={`#${quellePrefix}-${num}`}
            onClick={(e) => {
              e.preventDefault();
              document
                .getElementById(`${quellePrefix}-${num}`)
                ?.scrollIntoView({ behavior: "smooth" });
            }}
            style={{
              color: "var(--primaryColor)",
              textDecoration: "none",
              fontSize: "11px",
            }}
          >
            {label}
          </a>
        </sup>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
};

/**
 * Rendert einen Text mit Absätzen (\n\n), Aufzählungen (• oder -),
 * **bold**-Formatierung und [N]-Quellenverweisen.
 */
export const renderFormattedText = (
  text: string,
  quellePrefix: string = "quelle"
): React.ReactNode[] | null => {
  if (!text) return null;

  return text.split("\n\n").map((paragraph, index) => {
    // Aufzählungslisten erkennen (• oder -)
    if (paragraph.includes("\n•") || paragraph.startsWith("•")) {
      const lines = paragraph.split("\n");
      const intro = lines[0].startsWith("•") ? null : lines[0];
      const items = lines.filter((line) => line.trim().startsWith("•"));
      return (
        <div key={index}>
          {intro && <p>{processInlineFormatting(intro, quellePrefix)}</p>}
          <ul>
            {items.map((item, i) => (
              <li key={i}>
                {processInlineFormatting(
                  item.replace(/^•\s*/, ""),
                  quellePrefix
                )}
              </li>
            ))}
          </ul>
        </div>
      );
    }

    return (
      <p key={index}>{processInlineFormatting(paragraph, quellePrefix)}</p>
    );
  });
};
